import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  signal
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BlipChatService } from '@app-services/blip-chat.service';

const DATE_HINT_DELAY_MS = 700;
const EXPANDED_BODY_CLASS = 'blip-hint-expanded';

@Component({
  selector: 'app-blip-support-hint',
  standalone: true,
  templateUrl: './blip-support-hint.component.html',
  styleUrl: './blip-support-hint.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BlipSupportHintComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  protected readonly blipChatService = inject(BlipChatService);

  protected readonly showDateHint = signal(false);
  private dateHintTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const visible = this.blipChatService.isTooltipVisible();

      if (!visible) {
        this.resetDateHint();
        return;
      }

      this.scheduleDateHint();
    }, { allowSignalWrites: true });

    this.destroyRef.onDestroy(() => this.resetDateHint());
  }

  protected openSupport(): void {
    this.blipChatService.openChat();
  }

  protected dismiss(event: MouseEvent): void {
    event.stopPropagation();
    this.blipChatService.dismissTooltip();
  }

  private scheduleDateHint(): void {
    this.clearDateHintTimer();
    this.showDateHint.set(false);
    this.document.body.classList.remove(EXPANDED_BODY_CLASS);

    if (this.isMobileViewport()) {
      return;
    }

    this.dateHintTimer = setTimeout(() => {
      this.document.body.classList.add(EXPANDED_BODY_CLASS);
      this.showDateHint.set(true);
      this.dateHintTimer = null;
    }, DATE_HINT_DELAY_MS);
  }

  private resetDateHint(): void {
    this.clearDateHintTimer();
    this.showDateHint.set(false);
    this.document.body.classList.remove(EXPANDED_BODY_CLASS);
  }

  private clearDateHintTimer(): void {
    if (!this.dateHintTimer) {
      return;
    }

    clearTimeout(this.dateHintTimer);
    this.dateHintTimer = null;
  }

  private isMobileViewport(): boolean {
    return window.matchMedia('(max-width: 767px), (max-height: 420px)').matches;
  }
}